/**
 * Unit conversion utilities for both bike and running modules
 */

/**
 * Get the current units setting from localStorage
 * @returns {string} 'metric' or 'imperial'
 */
export const getUnits = () => localStorage.getItem('units') || 'metric';

// ==================== Distance Conversions ====================

/**
 * Convert meters to km or miles
 * @param {number} meters - Distance in meters
 * @param {string} units - 'metric' or 'imperial'
 * @returns {number} Distance in km or miles
 */
export const convertDistance = (meters, units) =>
  units === 'imperial' ? meters * 0.000621371 : meters / 1000;

/**
 * Format distance with appropriate units
 * @param {number} meters - Distance in meters
 * @param {string} units - 'metric' or 'imperial'
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted distance with unit
 */
export const formatDistance = (meters, units, decimals = 2) => {
  if (meters === null || meters === undefined) return 'N/A';
  const dist = convertDistance(meters, units);
  const unit = units === 'imperial' ? 'mi' : 'km';
  return `${dist.toFixed(decimals)} ${unit}`;
};

// ==================== Speed Conversions (for bikes) ====================

/**
 * Convert km/h to mph
 * @param {number} kmh - Speed in km/h
 * @param {string} units - 'metric' or 'imperial'
 * @returns {number} Speed in km/h or mph
 */
export const convertSpeed = (kmh, units) =>
  units === 'imperial' ? kmh * 0.621371 : kmh;

/**
 * Format speed with appropriate units
 * @param {number} kmh - Speed in km/h
 * @param {string} units - 'metric' or 'imperial'
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted speed with unit
 */
export const formatSpeed = (kmh, units, decimals = 1) => {
  if (kmh === null || kmh === undefined) return 'N/A';
  const speed = convertSpeed(kmh, units);
  const unit = units === 'imperial' ? 'mph' : 'km/h';
  return `${speed.toFixed(decimals)} ${unit}`;
};

// ==================== Pace Conversions (for running) ====================

/**
 * Convert pace from min/km to min/mile
 * @param {number} minPerKm - Pace in min/km
 * @param {string} units - 'metric' or 'imperial'
 * @returns {number} Pace in min/km or min/mile
 */
export const convertPace = (minPerKm, units) =>
  units === 'imperial' ? minPerKm * 1.60934 : minPerKm;

/**
 * Format pace as MM:SS/unit
 * @param {number} minPerKm - Pace in min/km
 * @param {string} units - 'metric' or 'imperial'
 * @returns {string} Formatted pace (e.g., "5:23/km" or "8:38/mi")
 */
export const formatPace = (minPerKm, units) => {
  if (minPerKm === null || minPerKm === undefined || minPerKm <= 0) return 'N/A';
  const pace = convertPace(minPerKm, units);
  const min = Math.floor(pace);
  const sec = Math.round((pace - min) * 60);
  const unit = units === 'imperial' ? '/mi' : '/km';
  return `${min}:${sec.toString().padStart(2, '0')}${unit}`;
};

/**
 * Parse pace string (MM:SS) to decimal minutes
 * @param {string} paceStr - Pace string like "5:23"
 * @returns {number} Pace in decimal minutes (e.g., 5.383)
 */
export const parsePace = (paceStr) => {
  if (!paceStr || typeof paceStr !== 'string') return null;
  const parts = paceStr.split(':');
  if (parts.length !== 2) return null;
  const min = parseInt(parts[0], 10);
  const sec = parseInt(parts[1], 10);
  if (isNaN(min) || isNaN(sec)) return null;
  return min + sec / 60;
};

// ==================== Elevation Conversions ====================

/**
 * Convert meters to feet
 * @param {number} meters - Elevation in meters
 * @param {string} units - 'metric' or 'imperial'
 * @returns {number} Elevation in meters or feet
 */
export const convertElevation = (meters, units) =>
  units === 'imperial' ? meters * 3.28084 : meters;

/**
 * Format elevation with appropriate units
 * @param {number} meters - Elevation in meters
 * @param {string} units - 'metric' or 'imperial'
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted elevation with unit
 */
export const formatElevation = (meters, units, decimals = 0) => {
  if (meters === null || meters === undefined) return 'N/A';
  const elev = convertElevation(meters, units);
  const unit = units === 'imperial' ? 'ft' : 'm';
  return `${elev.toFixed(decimals)} ${unit}`;
};

// ==================== Weight Conversions ====================

/**
 * Convert kg to lbs
 * @param {number} kg - Weight in kg
 * @param {string} units - 'metric' or 'imperial'
 * @returns {number} Weight in kg or lbs
 */
export const convertWeight = (kg, units) =>
  units === 'imperial' ? kg * 2.20462 : kg;

/**
 * Format weight with appropriate units
 * @param {number} kg - Weight in kg
 * @param {string} units - 'metric' or 'imperial'
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted weight with unit
 */
export const formatWeight = (kg, units, decimals = 1) => {
  if (kg === null || kg === undefined) return 'N/A';
  const weight = convertWeight(kg, units);
  const unit = units === 'imperial' ? 'lbs' : 'kg';
  return `${weight.toFixed(decimals)} ${unit}`;
};

// ==================== Temperature Conversions ====================

/**
 * Convert Celsius to Fahrenheit
 * @param {number} celsius - Temperature in Celsius
 * @param {string} units - 'metric' or 'imperial'
 * @returns {number} Temperature in Celsius or Fahrenheit
 */
export const convertTemperature = (celsius, units) =>
  units === 'imperial' ? (celsius * 9 / 5) + 32 : celsius;

/**
 * Format temperature with appropriate units
 * @param {number} celsius - Temperature in Celsius
 * @param {string} units - 'metric' or 'imperial'
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted temperature with unit
 */
export const formatTemperature = (celsius, units, decimals = 1) => {
  if (celsius === null || celsius === undefined) return 'N/A';
  const temp = convertTemperature(celsius, units);
  const unit = units === 'imperial' ? '°F' : '°C';
  return `${temp.toFixed(decimals)}${unit}`;
};

// ==================== Time Formatting ====================

/**
 * Format seconds as HH:MM:SS or MM:SS
 * @param {number} seconds - Duration in seconds
 * @param {boolean} includeHours - Force include hours even if 0 (default: auto)
 * @returns {string} Formatted duration
 */
export const formatDuration = (seconds, includeHours = null) => {
  if (seconds === null || seconds === undefined) return 'N/A';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const showHours = includeHours !== null ? includeHours : hrs > 0;

  if (showHours) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
};

/**
 * Format seconds as hours with decimal (e.g., 3.5h)
 * @param {number} seconds - Duration in seconds
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted hours
 */
export const formatHours = (seconds, decimals = 1) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  const hours = seconds / 3600;
  return `${hours.toFixed(decimals)}h`;
};

/**
 * Extract UTC offset in minutes from an ISO datetime string (e.g. "+02:00" → 120)
 * @param {string} isoString
 * @returns {number|null}
 */
export const getUtcOffsetMinutes = (isoString) => {
  if (!isoString) return null;
  const match = isoString.match(/([+-])(\d{2}):(\d{2})$/);
  if (!match) return null;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
};

/**
 * Format elapsed seconds since a workout/race start as "Day X HH:MM", using the
 * recording timezone embedded in workoutStartTime's UTC offset.
 * @param {number} elapsedSeconds - Seconds since start
 * @param {string} workoutStartTime - ISO datetime with UTC offset
 * @param {object} [opts]
 * @param {boolean} [opts.omitDayOne=false] - drop the "Day 1 " prefix (keep "Day 2+")
 * @returns {string} e.g. "Day 1 14:32" (or "14:32" with omitDayOne), or "-" if inputs are missing
 */
export const formatArrivalTime = (elapsedSeconds, workoutStartTime, { omitDayOne = false } = {}) => {
  if (elapsedSeconds == null || !workoutStartTime) return '-';
  const dayPrefix = (dayNum) => (omitDayOne && dayNum === 1 ? '' : `Day ${dayNum} `);
  const startMs = new Date(workoutStartTime).getTime();
  const arrivalMs = startMs + elapsedSeconds * 1000;

  const offsetMinutes = getUtcOffsetMinutes(workoutStartTime);
  if (offsetMinutes !== null) {
    // Shift to recording timezone
    const offsetMs = offsetMinutes * 60000;
    const arrivalLocalMs = arrivalMs + offsetMs;
    const startLocalMs = startMs + offsetMs;
    // Midnight of start day in recording timezone
    const startLocalDate = new Date(startLocalMs);
    const startMidnightLocalMs = startLocalMs - (
      startLocalDate.getUTCHours() * 3600000 +
      startLocalDate.getUTCMinutes() * 60000 +
      startLocalDate.getUTCSeconds() * 1000 +
      startLocalDate.getUTCMilliseconds()
    );
    const dayNum = Math.floor((arrivalLocalMs - startMidnightLocalMs) / 86400000) + 1;
    const arrivalLocalDate = new Date(arrivalLocalMs);
    const hh = String(arrivalLocalDate.getUTCHours()).padStart(2, '0');
    const mm = String(arrivalLocalDate.getUTCMinutes()).padStart(2, '0');
    return `${dayPrefix(dayNum)}${hh}:${mm}`;
  }

  // Fallback: browser local time
  const startMidnight = new Date(workoutStartTime);
  startMidnight.setHours(0, 0, 0, 0);
  const dayNum = Math.floor((arrivalMs - startMidnight.getTime()) / 86400000) + 1;
  const arrivalDate = new Date(arrivalMs);
  const hh = String(arrivalDate.getHours()).padStart(2, '0');
  const mm = String(arrivalDate.getMinutes()).padStart(2, '0');
  return `${dayPrefix(dayNum)}${hh}:${mm}`;
};

// ==================== Gradient Formatting ====================

/**
 * Format gradient as percentage
 * @param {number} gradient - Gradient as decimal (e.g., 0.05 for 5%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted gradient
 */
export const formatGradient = (gradient, decimals = 1) => {
  if (gradient === null || gradient === undefined) return 'N/A';
  return `${(gradient * 100).toFixed(decimals)}%`;
};

// ==================== Power Formatting (for bikes) ====================

/**
 * Format power in watts
 * @param {number} watts - Power in watts
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted power
 */
export const formatPower = (watts, decimals = 0) => {
  if (watts === null || watts === undefined) return 'N/A';
  return `${watts.toFixed(decimals)} W`;
};

/**
 * Format power as W/kg
 * @param {number} watts - Power in watts
 * @param {number} kg - Weight in kg
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted power per kg
 */
export const formatPowerPerKg = (watts, kg, decimals = 2) => {
  if (watts === null || watts === undefined || kg === null || kg === undefined || kg === 0) return 'N/A';
  return `${(watts / kg).toFixed(decimals)} W/kg`;
};

// ==================== Heart Rate Formatting ====================

/**
 * Format heart rate in bpm
 * @param {number} bpm - Heart rate in bpm
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted heart rate
 */
export const formatHeartRate = (bpm, decimals = 0) => {
  if (bpm === null || bpm === undefined) return 'N/A';
  return `${bpm.toFixed(decimals)} bpm`;
};

// ==================== Cadence Formatting ====================

/**
 * Format cadence in rpm (bikes) or spm (running)
 * @param {number} cadence - Cadence value
 * @param {string} sport - 'bike' or 'running'
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted cadence
 */
export const formatCadence = (cadence, sport = 'bike', decimals = 0) => {
  if (cadence === null || cadence === undefined) return 'N/A';
  const unit = sport === 'running' ? 'spm' : 'rpm';
  return `${cadence.toFixed(decimals)} ${unit}`;
};

// ==================== Vertical Speed Formatting (for running) ====================

/**
 * Format vertical speed in m/h or ft/h
 * @param {number} metersPerSecond - Vertical speed in m/s
 * @param {string} units - 'metric' or 'imperial'
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted vertical speed
 */
export const formatVerticalSpeed = (metersPerSecond, units, decimals = 0) => {
  if (metersPerSecond === null || metersPerSecond === undefined) return 'N/A';
  const metersPerHour = metersPerSecond * 3600;
  const value = convertElevation(metersPerHour, units);
  const unit = units === 'imperial' ? 'ft/h' : 'm/h';
  return `${value.toFixed(decimals)} ${unit}`;
};
